import { useState, useEffect, useCallback } from 'react';
import { BrowserProvider, JsonRpcSigner } from 'ethers';

interface WalletState {
  address: string | null;
  signer: JsonRpcSigner | null;
  provider: BrowserProvider | null;
  chainId: number | null;
  isConnecting: boolean;
  error: string | null;
}

const STORAGE_KEY = 'ternoa_shield_wallet_connected';

/** Create a BrowserProvider that won't throw on chain changes */
function makeProvider() {
  // `any` network tells ethers to detect & allow chain switches without throwing NETWORK_ERROR
  return new BrowserProvider(window.ethereum!, 'any');
}

export function useWallet() {
  const [state, setState] = useState<WalletState>({
    address: null,
    signer: null,
    provider: null,
    chainId: null,
    isConnecting: false,
    error: null,
  });

  const connect = useCallback(async () => {
    if (!window.ethereum) {
      setState((s) => ({ ...s, error: 'No wallet detected. Install MetaMask or Rabby.' }));
      return;
    }

    setState((s) => ({ ...s, isConnecting: true, error: null }));

    try {
      const provider = makeProvider();
      await provider.send('eth_requestAccounts', []);
      const signer = await provider.getSigner();
      const address = await signer.getAddress();
      const network = await provider.getNetwork();
      const chainId = Number(network.chainId);

      localStorage.setItem(STORAGE_KEY, 'true');
      setState({
        address,
        signer,
        provider,
        chainId,
        isConnecting: false,
        error: null,
      });
    } catch (err: any) {
      setState((s) => ({
        ...s,
        isConnecting: false,
        error: err.message || 'Failed to connect wallet',
      }));
    }
  }, []);

  const disconnect = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setState({
      address: null,
      signer: null,
      provider: null,
      chainId: null,
      isConnecting: false,
      error: null,
    });
  }, []);

  // Auto-reconnect on page load if previously connected
  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) === 'true' && window.ethereum && !state.address) {
      const provider = makeProvider();
      provider.send('eth_accounts', []).then((accounts: string[]) => {
        if (accounts.length > 0) {
          connect();
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      }).catch(() => {
        localStorage.removeItem(STORAGE_KEY);
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for account/chain changes
  useEffect(() => {
    if (!window.ethereum) return;

    const handleAccountsChanged = (accounts: string[]) => {
      if (accounts.length === 0) {
        disconnect();
      } else if (state.address) {
        // Account switched — reconnect to get new signer
        connect();
      }
    };

    const handleChainChanged = (chainIdHex: string) => {
      // Just update the chainId in state — we don't care which chain the user is on
      // Balances are read from Ternoa RPC directly, signing is chain-agnostic
      const newChainId = parseInt(chainIdHex, 16);
      setState((s) => (s.address ? { ...s, chainId: newChainId } : s));
    };

    window.ethereum.on('accountsChanged', handleAccountsChanged);
    window.ethereum.on('chainChanged', handleChainChanged);

    return () => {
      window.ethereum?.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum?.removeListener('chainChanged', handleChainChanged);
    };
  }, [state.address, connect, disconnect]);

  return {
    ...state,
    connect,
    disconnect,
    shortAddress: state.address
      ? `${state.address.slice(0, 6)}...${state.address.slice(-4)}`
      : null,
  };
}

// Extend Window for ethereum
declare global {
  interface Window {
    ethereum?: any;
  }
}
